import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { setMFAEnrollmentHandler, setUnauthenticatedHandler } from "./api/client";
import { useSession } from "./store/session";
import { Icono, type NombreIcono } from "./components/Icono";
import { Loading } from "./components/ui";

// El layout es también el guardia de la sesión: carga /auth/me antes de
// pintar nada y manda al login si no hay sesión.
//
// Va aquí y no en cada vista porque la autoridad es el backend (ADR-019): el
// token está en una cookie HttpOnly que este código no puede leer, así que
// "estoy autenticado" solo se sabe preguntando. Repartir esa pregunta por las
// vistas acaba con alguna que no la hace y muestra un error en lugar de llevar
// al login.
export function Layout() {
  const navigate = useNavigate();
  const { session, status, load, logout, can, marcarMFARequerido } = useSession();
  const [menuAbierto, setMenuAbierto] = useState(false);

  useEffect(() => {
    void load();
  }, [load]);

  // Una sesión que caduca a mitad de una navegación tiene que llevar al login
  // desde donde sea.
  useEffect(() => {
    setUnauthenticatedHandler(() => navigate("/login", { replace: true }));
    // Y si el rol exige segundo factor sin configurar, al alta: la sesión está
    // restringida a `/auth/*`, así que cualquier otra pantalla devolvería 403
    // sin decir qué hacer.
    setMFAEnrollmentHandler(() => {
      marcarMFARequerido();
      navigate("/security", { replace: true });
    });
  }, [navigate, marcarMFARequerido]);

  useEffect(() => {
    if (status === "anonymous") {
      navigate("/login", { replace: true });
    }
  }, [status, navigate]);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loading label="Cargando sesión…" />
      </div>
    );
  }
  if (status === "anonymous" || !session) {
    // El efecto de arriba ya está navegando; no se pinta nada intermedio.
    return null;
  }

  const visible = (item: ItemNav) =>
    item.superadminOnly ? session.user.is_superadmin : item.permission ? can(item.permission) : true;

  // Los grupos vacíos no se pintan: un encabezado de sección sin nada debajo
  // es peor que no tener secciones.
  const grupos = GRUPOS.map((g) => ({ ...g, items: g.items.filter(visible) })).filter(
    (g) => g.items.length > 0,
  );

  return (
    <div className="relative min-h-screen">
      <Fondo />

      {/* ------------------------------------------------------ encabezado -- */}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#05070f]/80 backdrop-blur-xl">
        <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              className="rounded-lg border border-white/10 p-1.5 text-slate-400 transition hover:bg-white/[0.06] hover:text-white lg:hidden"
              onClick={() => setMenuAbierto((v) => !v)}
              aria-label={menuAbierto ? "Cerrar el menú" : "Abrir el menú"}
              aria-expanded={menuAbierto}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
                <path d={menuAbierto ? "M6 6l12 12M18 6 6 18" : "M4 7h16M4 12h16M4 17h16"} />
              </svg>
            </button>
            <div className="flex items-baseline gap-2.5">
              <span className="bg-gradient-to-br from-white to-sky-200 bg-clip-text text-base font-semibold tracking-tight text-transparent">
                SeekopIA
              </span>
              <span className="hidden font-mono text-[0.65rem] uppercase tracking-[0.2em] text-sky-400/70 sm:inline">
                Voice API
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm">
            <TenantSwitcher />
            <span className="hidden text-slate-400 md:inline">{session.user.email}</span>
            <button
              className="rounded-lg border border-white/10 px-3 py-1.5 text-sm text-slate-400 transition hover:border-rose-500/40 hover:bg-rose-500/10 hover:text-rose-200"
              onClick={() => void logout().then(() => navigate("/login", { replace: true }))}
            >
              Salir
            </button>
          </div>
        </div>
      </header>

      <div className="flex gap-6 px-4 py-6 sm:px-6 lg:px-8">
        {/* ------------------------------------------------ barra lateral -- */}
        {/* En pantallas chicas se superpone; en grandes es una columna fija que
            no se desplaza con el contenido, para no perder el menú al bajar por
            una tabla larga. */}
        {menuAbierto && (
          <div
            className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm lg:hidden"
            onClick={() => setMenuAbierto(false)}
          />
        )}

        <nav
          className={`fixed inset-y-0 left-0 z-20 w-64 shrink-0 overflow-y-auto border-r border-white/10 bg-[#070a14] px-3 pb-6 pt-20 transition-transform lg:sticky lg:top-[4.5rem] lg:z-0 lg:h-[calc(100vh-6rem)] lg:translate-x-0 lg:rounded-xl lg:border lg:bg-white/[0.02] lg:pt-4 ${
            menuAbierto ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {grupos.map((grupo) => (
            <div key={grupo.titulo} className="mb-5">
              <p className="px-3 pb-2 font-mono text-[0.6rem] uppercase tracking-[0.18em] text-slate-600">
                {grupo.titulo}
              </p>
              <ul className="space-y-0.5">
                {grupo.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={() => setMenuAbierto(false)}
                      className={({ isActive }) =>
                        `group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition ${
                          isActive
                            ? "bg-gradient-to-r from-sky-500/20 to-transparent font-medium text-white"
                            : "text-slate-400 hover:bg-white/[0.05] hover:text-slate-100"
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {/* La barra encendida al costado: señala la pantalla
                              actual sin depender solo del color de fondo, que
                              en oscuro se distingue poco. */}
                          <span
                            className={`absolute inset-y-1.5 left-0 w-0.5 rounded-full transition ${
                              isActive
                                ? "bg-sky-400 shadow-[0_0_8px_1px_rgba(56,189,248,0.8)]"
                                : "bg-transparent"
                            }`}
                          />
                          <Icono
                            nombre={item.icono}
                            className={`h-[1.1rem] w-[1.1rem] shrink-0 transition ${
                              isActive ? "text-sky-300" : "text-slate-500 group-hover:text-slate-300"
                            }`}
                          />
                          {item.label}
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <main className="min-w-0 flex-1 pb-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** El mismo fondo de la pantalla de acceso, más apagado para no competir con
 *  los datos: aquí la gente viene a leer tablas, no a mirar el degradado. */
function Fondo() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
      <div className="absolute -left-40 -top-40 h-[32rem] w-[32rem] rounded-full bg-sky-500/15 blur-[90px]" />
      <div className="absolute -bottom-52 right-[-10rem] h-[34rem] w-[34rem] rounded-full bg-indigo-600/15 blur-[90px]" />
      <div className="rejilla absolute inset-0 opacity-60" />
    </div>
  );
}

type ItemNav = {
  to: string;
  label: string;
  icono: NombreIcono;
  permission?: string;
  superadminOnly?: boolean;
};

// Las entradas se filtran por permiso: un enlace a una vista que va a devolver
// 403 es peor que no tener el enlace.
//
// Y se agrupan por lo que alguien viene a hacer, no por el módulo del backend
// que las sirve: «operación» es el día a día, «configuración» lo que se toca de
// vez en cuando, y «plataforma» lo que solo ve OMITECH.
const GRUPOS: { titulo: string; items: ItemNav[] }[] = [
  {
    titulo: "Operación",
    items: [
      { to: "/dashboard", label: "Tablero", icono: "tablero", permission: "calls.read" },
      // «En curso» va antes que «Llamadas» a propósito: lo que está pasando
      // ahora se consulta más veces al día que el histórico, y estaba
      // escondido dentro del tablero.
      { to: "/live", label: "En curso", icono: "en-curso", permission: "calls.read" },
      { to: "/calls", label: "Llamadas", icono: "llamadas", permission: "calls.read" },
      { to: "/playground", label: "Playground", icono: "playground", permission: "calls.create" },
    ],
  },
  {
    titulo: "Configuración",
    items: [
      { to: "/voice-apps", label: "Aplicaciones de voz", icono: "apps", permission: "voice_apps.read" },
      { to: "/numbers", label: "Números", icono: "numeros", permission: "numbers.read" },
      { to: "/caller-ids", label: "Caller IDs", icono: "callerid", permission: "caller_ids.manage" },
      { to: "/webhooks", label: "Webhooks", icono: "webhooks", permission: "webhooks.read" },
      { to: "/limits", label: "Límites", icono: "limites", permission: "limits.read" },
    ],
  },
  {
    titulo: "Cuenta",
    items: [
      { to: "/balance", label: "Saldo", icono: "saldo", permission: "billing.read" },
      { to: "/usage", label: "Consumo", icono: "consumo", permission: "billing.read" },
      { to: "/api-keys", label: "Llaves de API", icono: "llaves", permission: "api_keys.manage" },
      { to: "/users", label: "Usuarios", icono: "usuarios", permission: "users.manage" },
      // Seguridad no lleva permiso: el segundo factor es del usuario, no de la
      // cuenta, y todo el mundo tiene que poder llegar a configurarlo. Es
      // también la única pantalla accesible cuando la sesión está restringida
      // por no tener MFA todavía.
      { to: "/security", label: "Seguridad", icono: "seguridad" },
    ],
  },
  {
    titulo: "Plataforma",
    items: [
      { to: "/tenants", label: "Cuentas", icono: "cuentas", superadminOnly: true },
      { to: "/rates", label: "Tarifas", icono: "tarifas", superadminOnly: true },
      { to: "/trunks", label: "Troncales", icono: "troncales", superadminOnly: true },
      { to: "/nodes", label: "Nodos", icono: "nodos", superadminOnly: true },
    ],
  },
];

/**
 * TenantSwitcher deja cambiar de cuenta activa.
 *
 * Solo aparece con más de una: con una sola, un selector de un elemento es
 * ruido. El cambio lo hace el backend (`/auth/switch-tenant`) porque la cuenta
 * activa vive en la sesión del servidor, no en el cliente — si viviera aquí,
 * cambiarla sería tan fácil como editar el almacenamiento del navegador.
 */
function TenantSwitcher() {
  const { session, switchTenant } = useSession();
  if (!session) return null;

  // Un superadministrador ve siempre el selector, aunque pertenezca a una
  // sola cuenta: necesita poder soltarla. Las pantallas de inventario —los
  // números sin dueño, por ejemplo— solo existen sin cuenta activa, y al
  // darle membresía en una cuenta para ayudarla se quedaba encerrado en ella.
  const esPlataforma = session.user.is_superadmin;
  if (!esPlataforma && session.tenants.length <= 1) return null;

  return (
    <select
      className="max-w-[12rem] rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-sm text-slate-200 transition focus:border-sky-400/60 focus:outline-none [&>option]:bg-[#11162a]"
      value={session.active_tenant_id}
      onChange={(e) => void switchTenant(e.target.value)}
      aria-label="Cuenta activa"
    >
      {esPlataforma && <option value="">— plataforma —</option>}
      {session.tenants.map(({ tenant }) => (
        <option key={tenant.id} value={tenant.id}>
          {tenant.name}
        </option>
      ))}
    </select>
  );
}
